import {
    bexHttpClient
} from '../../clients';
import type {
    ConversationListResponse,
    ConversationSessionListResponse,
    ConversationUserListResponse,
    ConversationTrackingSessionListResponse
} from '../bexService.types';

/**
 * Service class for conversation history operations.
 * All endpoints are POST and require a valid JWT.
 * Data is retrieved from Redis — exact object shapes depend on what Project O writes.
 */
class ConversationService {

    /**
     * List conversations by organization, sub-organization, and channel.
     * POST /cm/redis/conversations/list
     */
    async listConversations(
        organizationId: string,
        subOrganizationId: string,
        channelAddress: string
    ): Promise<ConversationListResponse> {
        const response = await bexHttpClient.post('/redis/conversations/list', {
            organizationId,
            subOrganizationId,
            channelAddress
        });
        return response;
    }

    /**
     * List conversation sessions (with user info, status, etc).
     * POST /cm/redis/conversations/sessions/list
     * channelAddress is optional — omit to get sessions across all channels.
     */
    async listSessions(
        organizationId: string,
        subOrganizationId: string,
        channelAddress?: string
    ): Promise<ConversationSessionListResponse> {
        const body: Record<string, string> = {
 organizationId,
subOrganizationId
};
        if (channelAddress) body.channelAddress = channelAddress;
        const response = await bexHttpClient.post('/redis/conversations/sessions/list', body);
        return response;
    }

    /**
     * List conversation users across the org/sub-org.
     * POST /cm/redis/conversations/users/list
     * No channelAddress param — returns all users org-wide.
     */
    async listUsers(
        organizationId: string,
        subOrganizationId: string
    ): Promise<ConversationUserListResponse> {
        const response = await bexHttpClient.post('/redis/conversations/users/list', {
            organizationId,
            subOrganizationId
        });
        return response;
    }

    /**
     * List conversation tracking sessions.
     * POST /cm/redis/conversations/tracking-sessions/list
     * channelAddress is optional — omit to get tracking sessions across all channels.
     */
    async listTrackingSessions(
        organizationId: string,
        subOrganizationId: string,
        channelAddress?: string
    ): Promise<ConversationTrackingSessionListResponse> {
        const body: Record<string, string> = {
 organizationId,
subOrganizationId
};
        if (channelAddress) body.channelAddress = channelAddress;
        const response = await bexHttpClient.post('/redis/conversations/tracking-sessions/list', body);
        return response;
    }
}

const conversationService = new ConversationService();
export { conversationService };
